import * as React from 'react';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {useField} from 'formik';
import {Formik} from 'formik';
import {Box, Chip, Dialog, DialogActions, DialogContent, Grid, Paper, Typography} from '@mui/material';
import Button from '@mui/material/Button';
import {AddOutlined} from '@mui/icons-material';
import {useAppDispatch} from 'store';
import {useAppTranslation} from 'services/i18n';
import {OptionValue} from 'model/form';
import {SelectFormField} from 'components/form/SelectFormField';
import {COMPETENCE_LEVELS} from 'model/approval';
import {fetchCompetences} from 'store/competence';
import {selectCompetences} from 'store/selectors';
import {defaultDialogStyle} from 'utils/dialog';

export interface CompetenceLevelValue {
    id?: number;
    competence_level?: number;
}

const dialogStyle = defaultDialogStyle(480);

/** Chip/Kompetence from the design system, in tokens so a recoloured space keeps it */
const competenceChipStyle = {
    // the class selector is needed: MUI's own filled background would otherwise
    // out-order the plain declaration
    '&.MuiChip-root, &.MuiChip-root:hover': {
        height: '22px',
        fontSize: '12px',
        backgroundColor: 'var(--color-primary15)',
        color: 'var(--color-primary60)'
    },
    '& .MuiChip-deleteIcon': {fontSize: '14px', color: 'var(--color-primary60)', opacity: .6}
};

/**
 * The block reads as one field: label, the chips on the input line, and the same
 * underline the neighbouring fields draw. The min-height matches a standard
 * `SelectFormField` row so a row of these keeps one baseline.
 */
const chipRowStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '4px 8px',
    minHeight: '32px',
    paddingBottom: '1px',
    borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
};

/** Chip/Přidat -- the outlined twin that opens the dialog */
const addChipStyle = {
    height: '22px',
    fontSize: '12px',
    '& .MuiChip-icon': {fontSize: '14px', marginLeft: '8px'}
};

/**
 * Competences with a target level, as `ApprovalRequestCreate.competences` takes
 * them. Unlike `CompetenceSelectFormField` -- which only collects ids and lets
 * the API default the level -- each pair here carries its own level, because a
 * self-submitted request is refused without one
 * (`Create#competences_required_but_missing?`).
 *
 * Drawn the way the design does it: every picked pair is a chip, and both adding
 * and editing happen in a dialog, so the two selects never have to share a row.
 */
export const CompetenceLevelFormField = ({name, label, required, error}: {
    name: string,
    label: string,
    required?: boolean,
    error?: string,
}) => {
    const t = useAppTranslation();
    const dispatch = useAppDispatch();

    const [field, , helpers] = useField<CompetenceLevelValue[]>(name);
    const rows = useMemo(() => field.value || [], [field.value]);
    const setRows = helpers.setValue;

    /** the pair being edited; `index === rows.length` is a new one */
    const [edited, setEdited] = useState<{index: number, value: CompetenceLevelValue}>();

    const {items, isLoading} = useSelector(selectCompetences);

    useEffect(() => {
        dispatch(fetchCompetences());
    }, [dispatch]);

    // a competence already picked elsewhere would create a duplicate the API refuses
    const options: OptionValue[] = useMemo(() => {
        const taken = rows.filter((row, i) => i !== edited?.index).map(row => row.id);
        return items
            .filter(c => taken.indexOf(c.id) < 0)
            .map(c => ({value: c.id, label: c.name, tooltip: c.competence_type}));
    }, [items, rows, edited?.index]);

    const levelOptions: OptionValue[] = useMemo(() => COMPETENCE_LEVELS.map(level => ({
        value: level, label: t('approvals.competenceLevel.' + level)
    })), [t]);

    const chipLabel = useCallback((row: CompetenceLevelValue) => {
        const competence = items.find(c => c.id === row.id);
        const title = competence?.name || String(row.id ?? '');
        return row.competence_level
            ? title + ' · ' + t('approvals.form.competenceLevelShort', {level: row.competence_level})
            : title;
    }, [items, t]);

    const handleRemove = useCallback((index: number) => {
        setRows(rows.filter((row, i) => i !== index));
    }, [rows, setRows]);

    const handleSave = useCallback((value: CompetenceLevelValue) => {
        if (!edited) {
            return;
        }
        const next = [...rows];
        next[edited.index] = value;
        setRows(next);
        setEdited(undefined);
    }, [edited, rows, setRows]);

    const isNew = !!edited && edited.index === rows.length;

    return <Box sx={{margin: '8px 0 4px'}}>
        {/* a hand-built field, so its label carries the MuiFormLabel colour itself */}
        <Typography component={'div'} sx={{fontSize: '12px', color: 'var(--color-black)'}}>
            {label}{required ? ' *' : ''}
        </Typography>
        <Box sx={chipRowStyle}>
            {rows.map((row, index) => <Chip key={index} size={'small'} sx={competenceChipStyle}
                label={chipLabel(row)}
                onClick={() => setEdited({index, value: row})}
                onDelete={() => handleRemove(index)}/>)}
            <Chip variant={'outlined'} size={'small'} sx={addChipStyle}
                icon={<AddOutlined fontSize={'small'}/>}
                label={t('approvals.form.addCompetence')}
                onClick={() => setEdited({index: rows.length, value: {}})}/>
        </Box>
        {!!error && <Typography component={'div'} sx={{fontSize: '12px', color: 'var(--color-error)', paddingTop: '4px'}}>{error}</Typography>}

        {!!edited && <Dialog open={true} onClose={() => setEdited(undefined)} PaperComponent={Paper} sx={dialogStyle}>
            <Formik initialValues={edited.value} onSubmit={handleSave}>
                {({handleSubmit, values}) => <form onSubmit={handleSubmit}>
                    <DialogContent>
                        <Grid container spacing={1} className={'tw-mt-8 tw-pt-8'}>
                            <Grid size={12}>
                                {/* the field names are the dialog's own, not the form's array path */}
                                <SelectFormField name={'id'} shrink
                                    label={t('approvals.field.competence')}
                                    placeholder={t('approvals.form.addCompetence')}
                                    options={options} isLoading={isLoading}/>
                            </Grid>
                            <Grid size={12}>
                                <SelectFormField name={'competence_level'} shrink
                                    label={t('approvals.field.competenceLevel')}
                                    placeholder={t('approvals.form.addCompetenceLevel')}
                                    options={levelOptions}/>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions sx={{padding: '16px'}}>
                        <Button color={'inherit'} onClick={() => setEdited(undefined)}>{t('form.cancel')}</Button>
                        <Button color={'primary'} variant={'contained'} type={'submit'}
                            disabled={!values.id || !values.competence_level}>
                            {t(isNew ? 'form.add' : 'form.save')}
                        </Button>
                    </DialogActions>
                </form>}
            </Formik>
        </Dialog>}
    </Box>;
};
